<script setup>
import { ref, onMounted, computed, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useNotificationContactsStore } from '@/stores/notificationContactsStore';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { useI18n } from 'vue-i18n';
import { useSnackbarStore } from '@es-bill/es-bill-common-components';
import ContactDataFields from '@/components/molecule/ContactDataFields.vue';
import { toContactData } from '@/utils/contactDataUtils.js';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const notificationContactsStore = useNotificationContactsStore();
const communitiesStore = useCommunitiesStore();
const formValidation = inject('$formValidation');
const snackbarStore = useSnackbarStore();

// Route param and selected ECID for community
const ecidParam = route.params.ecid;
const selectedEcid = ref(ecidParam || '');
const contactId = route.params.contactId || null;
const existingContact = ref(null);
// form reference for explicit validation
const formRef = ref(null);

const valid = ref(false);
const contact = ref({ salutation: 'NONE' });

// Custom filter function for community autocomplete
const communityFilter = (itemText, queryText, item) => {
  if (!queryText) return true;
  
  const query = queryText.toLowerCase();
  const name = item.raw.name?.toLowerCase() || '';
  const ecid = item.raw.ecid?.toLowerCase() || '';
  
  return name.includes(query) || ecid.includes(query);
};

// Add computed list for rich community autocomplete
const communitiesAvailable = computed(() =>
  communitiesStore.allCommunities.map(c => ({
    ...c,
    title: c.name,
    subtitle: c.ecid,
    value: c.ecid
  }))
);

async function loadExisting() {
  if (contactId) {
    // First ensure we have all contacts loaded
    await notificationContactsStore.fetchNotificationContacts();

    // Find the contact in the store by _id
    const found = notificationContactsStore.contacts.find(c => c._id === contactId);
    if (found) {
      existingContact.value = found;
      contact.value = toContactData(found);
    }
  }
}

async function submitForm() {
  // trigger validation on all fields
  if (formRef.value) {
    formRef.value.validate();
  }
  // stop if form invalid
  if (!valid.value) {
    return;
  }
  const data = toContactData(contact.value);

  try {
    if (existingContact.value) {
      // updating existing - use contact's actual ecid instead of URL param to avoid 404
      await notificationContactsStore.updateNotificationContact(existingContact.value.ecid, contactId, data);
      snackbarStore.showSnackbar(t('applicantMessages.updateSuccess'), '', 'Success', 4000, []);
    } else {
      // creating new for selected community
      await notificationContactsStore.createNotificationContact(selectedEcid.value, data);
      snackbarStore.showSnackbar(t('requestSuccess'), '', 'Success', 4000, []);
    }
    router.push({ name: 'notificationContacts' });
  } catch (error) {
    snackbarStore.showSnackbar(error?.response?.data?.message || t('unexpectedError'), '', 'Error', 10000, []);
  }
}

onMounted(loadExisting);
</script>

<template>
  <v-container>
    <v-form ref="formRef" v-model="valid" lazy-validation>
      <h1>{{ existingContact ? t('editNotificationContact') : t('newNotificationContact') }}</h1>
      <!-- Community selection for new contacts -->
      <v-autocomplete
        v-if="!existingContact"
        :items="communitiesAvailable"
        item-title="title"
        item-value="value"
        v-model="selectedEcid"
        :rules="[formValidation.formValidationRequired]"
        :label="t('community')"
        :custom-filter="communityFilter"
        required
      >
        <template v-slot:item="{ props, item }">
          <v-list-item v-bind="props" :title="item.raw.title" :subtitle="item.raw.subtitle">
          </v-list-item>
        </template>
      </v-autocomplete>
      <v-text-field
        v-else
        :label="t('community')"
        :value="(communitiesStore.allCommunities.find(c => c.ecid === ecidParam) || {}).name"
        disabled
      />


      <ContactDataFields v-model="contact" require-name />

      <PrimaryButton @click="submitForm" :text="t('submit')" />
    </v-form>
  </v-container>
</template>

<style scoped>
</style>
